import * as React from 'react';
import styled from 'styled-components';

interface ICardProps {
  children: React.ReactNode;
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
  inverse?: boolean;
  elevation?: number;
  size?: CardSize;
  noBottomRadius?: boolean;
  noTopRadius?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tag?: any;
  id?: string;
}

/**
 * @description available sizes for the card (default is 100%)
 */
export type CardSize = 'small' | 'medium' | 'large' | 'full';

/**
 * @description gets the height of the card based on the `size` prop
 * @param props
 */
const getHeight = (props: { size?: CardSize }): string => {
  switch (props?.size) {
    case 'small':
      return '40vh';
    case 'medium':
      return '85vh';
    case 'large':
      return '98vh';
    case 'full':
      return '100vh';
    default:
      return '100%';
  }
};
/**
 * @description gets the width of the card based on the `size` prop
 * @param props
 */
const getWidth = (props: { size?: CardSize }): string => {
  switch (props?.size) {
    case 'small':
      return '40vw';
    case 'medium':
      return '85vw';
    case 'large':
      return '98vw';
    case 'full':
      return '100vw';
    default:
      return '100%';
  }
};

/**
 * @description outer most element of the card
 */
const CardWrapper = styled.div<{
  inverse?: boolean;
  elevation?: number;
  size?: CardSize;
  noTopRadius?: boolean;
  noBottomRadius?: boolean;
}>`
		border-radius: 10px;
		border-bottom-left-radius: ${(props: { noBottomRadius?: boolean }): string =>
      props?.noBottomRadius ? '0px' : '10px'};
	border-bottom-right-radius: ${(props: { noBottomRadius?: boolean }): string =>
    props?.noBottomRadius ? '0px' : '10px'};
		border-top-left-radius: ${(props: { noTopRadius?: boolean }): string =>
      props?.noTopRadius ? '0px' : '10px'};
		border-top-right-radius: ${(props: { noTopRadius?: boolean }): string =>
      props?.noTopRadius ? '0px' : '10px'};
	border: ${(props: { inverse?: boolean }): string =>
    props?.inverse ? '1px solid var(--color-separator)' : 'none'};
	background: ${(props: { inverse?: boolean; elevation?: number }): string =>
    props.inverse
      ? 'var(--color-card-background-inverse)'
      : props.elevation
      ? `var(--color-surface-${props.elevation.toString()})`
      : 'var(--color-surface-3)'};
	display: flex;
	position: relative;
	box-shadow: ${(props: { elevation?: number }): string =>
    typeof props?.elevation === 'number'
      ? `var(--shadow-elevation-${props.elevation.toString()})`
      : 'var(--shadow-elevation-3)'};

	overflow: hidden;
	height: ${getHeight};
	width: ${getWidth};
	@media (max-width: 991px) {
		height: ${(props: { size?: CardSize }): string =>
      props.size ? '100vh' : '100%'};
		width: ${(props: { size?: CardSize }): string =>
      props.size ? '100vw' : '100%'};
		min-height: 400px;
	}
`;

/**
 * @description modified outer element when the card is clickable
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ClickableCardWrapper = styled(CardWrapper as any)`
  cursor: pointer;
`;

const Card = (props: ICardProps): React.ReactElement => {
  const Wrapper = props.onClick ? ClickableCardWrapper : CardWrapper;
  return (
    <Wrapper
      as={props.tag ? props.tag : 'div'}
      onClick={props.onClick}
      style={props.style}
      className={props.className}
      inverse={props.inverse}
      elevation={props.elevation}
      size={props.size}
      noBottomRadius={props.noBottomRadius}
      noTopRadius={props.noTopRadius}
      id={props.id}
    >
      {props.children}
    </Wrapper>
  );
};

export { Card };
